import { HallOfRecords, HallView, battleMode, dailyRoadFromSave, gameEvents, recordsFromSave, recordsSection, ticksToMs } from "@highmarch/app";
import { LevelPhase, LevelSim } from "@highmarch/domain";
import { showRecordsSheet } from "../ui/records/recordsSheet";
import { recordsCardModel, recordsShareText, renderRecordsCard } from "../ui/records/recordsShare";
import { ShareHost, canShareCard, setShareRecordLines, shareCard } from "../ui/shareCard";
import { setCommanderRecords } from "../ui/hub/commanderSheet";
import { fmtClear } from "../ui/modes/challengeWords";
import { toast } from "../ui/toast";
import { ACHIEVEMENTS_ORDER } from "./achievements";
import type { Feature, FeatureContext } from "./registry";

/** The ⚙ menu slot: between Achievements (30) and the Almanac (40). */
const RECORDS_ORDER = ACHIEVEMENTS_ORDER + 5;
/** The Guide topic opens with the hub (after level 1), like Achievements'. */
const TOPIC_FROM_LEVEL = 2;

/** The game's hall, once the feature registers: Captain Bram's line waits for its first record (features/guide.ts). */
let theHall: HallOfRecords | null = null;
let hallClock: () => number = () => 0;
/** Whether the Hall has any record yet (Captain Bram's first line about it). */
export function recordsAny(): boolean { return theHall?.view(hallClock()).hasAny ?? false; }
/** The game's hall (the challenge codes feature records into it). */
export function hallOfRecords(): HallOfRecords | null { return theHall; }

/** The end card's line about a won campaign or farm run: a new fastest clear, or the run's time against the record. */
function recordLine(ctx: FeatureContext, hall: HallOfRecords, sim: LevelSim): string[] {
  if (sim.phase !== LevelPhase.Won) return [];
  // a challenge code's best (its end-screen block has recorded this run already)
  const code = sim.mods.challenge?.code, top = code ? hall.state.challenge(code) : null;
  if (code) return top?.won ? [`Best ${fmtClear(top.ms)} · can you beat it?`] : [];
  const mode = battleMode(ctx.catalog, sim.levelNumber, sim.modifiers, sim.firstClear);
  if (mode !== "campaign" && mode !== "farm") return [];
  const ms = ticksToMs(sim.tick), best = hall.state.clearsOf(sim.levelNumber)[mode];
  return [best === undefined || ms < best ? `New record · ${fmtClear(ms)}` : `${fmtClear(ms)} · record ${fmtClear(best)}`];
}

/**
 * The Hall of Records (MP-27, docs/MODES.md §8): the `records` save section bound to the save and backfilled once, the
 * game events it listens to (`battleWon`, `raceFinished`), the Endless bests synced after each Endless battle, ⚙ →
 * Hall of Records (beside Achievements and the Almanac) and its button on the Commander sheet's honours card, the
 * records share card and the end card's record line, a Guide topic, and debug hooks for captures.
 */
export const recordsFeature: Feature = {
  id: "records",
  register(ctx, registry) {
    const hall = new HallOfRecords(ctx.catalog, recordsFromSave(ctx.save), () => dailyRoadFromSave(ctx.save));
    theHall = hall;
    hallClock = () => ctx.now();
    recordsSection.bind(ctx.save, () => hall.state.toJson());
    if (hall.backfill(ctx.now())) ctx.persist();
    hall.attach(() => ctx.persist());
    registry.battleEnd((b) => { if (b.sim.isEndless && hall.syncEndless(ctx.now()).newBest) ctx.persist(); });
    setShareRecordLines((sim) => recordLine(ctx, hall, sim));

    const host: ShareHost | null = typeof navigator !== "undefined" && canShareCard(navigator) ? navigator : null;
    const open = (parent: HTMLElement): void => {
      const v = hall.view(ctx.now()), model = recordsCardModel(v);
      // the picture is drawn as the sheet opens, so SHARE reaches the share sheet inside its own tap (Safari)
      let png: Blob | null = null;
      if (host && v.hasAny) void renderRecordsCard(model).then((b) => { png = b; });
      const share = host ? (view: HallView) => {
        void shareCard(host, recordsShareText(recordsCardModel(view)), png).then((r) => {
          if (r === "copied") toast(parent, "Copied — paste it anywhere");
          else if (r === "failed") toast(parent, "Can't share from here");
        });
      } : null;
      showRecordsSheet(parent, { balance: ctx.balance, hall, now: ctx.now, share });
    };
    setCommanderRecords(open);

    registry.moreRow({
      id: "records", order: RECORDS_ORDER, icon: "records",
      label: () => "Hall of Records",
      note: () => {
        const v = hall.view(ctx.now());
        if (!v.hasAny) return "";
        if (v.endless.best.waves > 0) return `Endless wave ${v.endless.best.waves}`;
        return v.fastest ? `Fastest clear ${fmtClear(v.fastest.ms)}` : `${v.stars.have} stars`;
      },
      open: (hub) => open(hub.parent),
    });

    registry.guideTopic({
      id: "records", group: "castle", label: "Hall of Records", art: { icon: "trophy" }, fromLevel: TOPIC_FROM_LEVEL, steps: ["hub.records"],
      lines: [
        "The **Hall of Records** keeps your bests: Endless, Iron stars, your fastest clears, Race Week medals and challenge codes.",
        "Find it in the **⚙ menu** or on the **Commander** card. Share a card of your bests with friends.",
      ],
    });

    if (ctx.debug && typeof window !== "undefined") {
      // Captures: open the sheet, feed synthetic events (Race Week is another package's), see the view.
      (window as unknown as { __tdRecords: unknown }).__tdRecords = {
        hall, open: () => open(ctx.ui), view: () => hall.view(ctx.now()), persist: () => ctx.persist(),
        race: (week: string, level: number, ms: number, medal: "" | "bronze" | "silver" | "gold") => gameEvents.emit("raceFinished", { week, level, ms, medal, key: `debug:${week}:${ms}`, at: ctx.now() }),
        won: (mode: string, level: number, ms: number) => gameEvents.emit("battleWon", { mode, level, ms, stars: 3, key: `debug:${mode}:${level}:${ms}`, at: ctx.now() }),
        /** The records card as a picture (a data URL), for captures. */
        card: async () => { const b = await renderRecordsCard(recordsCardModel(hall.view(ctx.now()))); return b ? URL.createObjectURL(b) : null; },
      };
    }
  },
};
